import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { generatePDF } from '../utils/pdfGenerator';
import { predictDelays } from '../utils/api';
import { useTheme } from '../ThemeContext';

const CTooltip = ({ active, payload, label, dark }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: dark ? '#0d1929' : '#fff',
      border: `1px solid ${dark?'rgba(0,212,255,0.25)':'rgba(0,120,200,0.2)'}`,
      padding: '10px 14px', borderRadius: 6,
      fontFamily: "'Inter',sans-serif",
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    }}>
      <div style={{ color: dark?'#6090b0':'#7090a0', fontSize: 11, marginBottom: 5 }}>Sample #{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, fontSize: 13, fontWeight: 600 }}>
          {p.name}: <strong>{p.value}</strong> ms
        </div>
      ))}
    </div>
  );
};

export default function Predict() {
  const { dark } = useTheme();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [dlFlash, setDlFlash] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'text/csv': ['.csv'] },
    onDrop: f => { setFile(f[0]); setResults(null); setError(null); },
    multiple: false,
  });

  const handlePredict = async () => {
    if (!file) return;
    setLoading(true); setError(null); setResults(null); setSummary(null);
    const t0 = Date.now();
    const timer = setInterval(() => setElapsed(((Date.now()-t0)/1000).toFixed(1)), 100);
    try {
      const data = await predictDelays(file);
      setResults(data.results); setSummary(data.summary);
    } catch(e) { setError(e.message); }
    finally { clearInterval(timer); setElapsed(((Date.now()-t0)/1000).toFixed(1)); setLoading(false); }
  };

  const handleDownloadSample = () => {
    setDlFlash(true);
    setTimeout(() => setDlFlash(false), 1200);
    // triggers browser download from /public/TE1.csv
    const a = document.createElement('a');
    a.href = '/TE1.csv';
    a.download = 'TE1_sample_5G_traces.csv';
    a.click();
  };

  // theme
  const bg      = dark ? '#060c18' : '#f4f8ff';
  const border  = dark ? 'rgba(0,212,255,0.13)' : 'rgba(0,120,200,0.13)';
  const accent  = dark ? '#00d4ff' : '#0078c8';
  const text1   = dark ? '#e8f4ff' : '#0a1e3d';
  const text2   = dark ? '#8ab4d4' : '#3a5a7a';
  const text3   = dark ? '#4a7090' : '#7090b0';
  const gridC   = dark ? 'rgba(0,212,255,0.04)' : 'rgba(0,120,200,0.05)';
  const cardBg  = dark ? '#0d1929' : '#ffffff';
  const cardShad= dark ? 'none' : '0 2px 16px rgba(0,100,200,0.07)';
  const lineA   = dark ? '#ff5577' : '#dc2052';
  const btnClip = 'polygon(10px 0%,100% 0%,calc(100% - 10px) 100%,0% 100%)';

  const CARDS = summary ? [
    { l:'Total Predictions', v: summary.total_predictions, u:'' },
    { l:'Avg Delay',         v: summary.avg_predicted_delay, u:'ms' },
    { l:'Peak Delay',        v: summary.peak_predicted_delay, u:'ms' },
    { l:'Min Delay',         v: summary.min_predicted_delay, u:'ms' },
    { l:'MAE',               v: summary.mae, u:'ms' },
    { l:'RMSE',              v: summary.rmse, u:'ms' },
  ] : [];

  const chartData = results?.slice(0,100).map(r=>({sn:r.sn,Actual:r.actual,Predicted:r.predicted}));
  const residData = results?.slice(0,100).map(r=>({sn:r.sn,Residual:parseFloat((r.actual-r.predicted).toFixed(4))}));

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Inter:wght@300;400;500;600;700&display=swap');
        *{box-sizing:border-box;}
        .pr{
          background:${bg};color:${text1};
          font-family:'Inter',sans-serif;
          min-height:100vh;padding-top:62px;
          position:relative;overflow-x:hidden;
          transition:background 0.35s,color 0.35s;
        }
        .pr::before{
          content:'';position:fixed;inset:0;
          background-image:linear-gradient(${gridC} 1px,transparent 1px),linear-gradient(90deg,${gridC} 1px,transparent 1px);
          background-size:52px 52px;pointer-events:none;z-index:0;
        }

        .p-hero{
          position:relative;z-index:1;height:200px;overflow:hidden;
          display:flex;align-items:center;padding:0 40px;
        }
        .p-hero-bg{
          position:absolute;inset:0;
          background-image:url('/pic4.jpg');
          background-size:cover;background-position:center 60%;
          filter:${dark?'brightness(0.25) saturate(0.9)':'brightness(0.2) saturate(0.7)'};
        }
        .p-hero-ov{
          position:absolute;inset:0;
          background:${dark?'linear-gradient(90deg,rgba(6,12,24,0.9) 50%,transparent)':'linear-gradient(90deg,rgba(244,248,255,0.92) 55%,transparent)'};
        }
        .p-hero-c{position:relative;z-index:1;}
        .p-eyebrow{font-size:11px;font-weight:600;letter-spacing:3px;color:${accent};text-transform:uppercase;margin-bottom:6px;}
        .p-title{font-family:'Orbitron',monospace;font-size:clamp(22px,4vw,40px);font-weight:900;color:${text1};}
        .p-sub{font-size:14px;color:${text2};margin-top:4px;}

        .pi{position:relative;z-index:1;max-width:960px;margin:0 auto;padding:40px 24px 80px;}

        /* ── Sample file banner ── */
        .sample-banner{
          border:1px solid ${dark?'rgba(0,212,255,0.18)':'rgba(0,120,200,0.2)'};
          background:${dark?'rgba(0,212,255,0.04)':'rgba(0,120,200,0.04)'};
          border-radius:8px;padding:18px 20px;
          display:flex;align-items:center;justify-content:space-between;
          gap:16px;margin-bottom:20px;
          flex-wrap:wrap;
          position:relative;overflow:hidden;
        }
        .sample-banner::before{
          content:'';position:absolute;top:0;left:0;right:0;height:2px;
          background:linear-gradient(90deg,${accent},transparent);
        }
        .sample-left{display:flex;align-items:center;gap:14px;flex:1;min-width:220px;}
        .sample-icon{
          width:44px;height:44px;border-radius:8px;flex-shrink:0;
          background:${dark?'rgba(0,212,255,0.1)':'rgba(0,120,200,0.1)'};
          border:1px solid ${dark?'rgba(0,212,255,0.25)':'rgba(0,120,200,0.25)'};
          display:flex;align-items:center;justify-content:center;font-size:20px;
        }
        .sample-title{font-size:13px;font-weight:700;color:${text1};margin-bottom:3px;}
        .sample-meta{font-size:11px;color:${text3};}
        .sample-pills{display:flex;gap:6px;flex-wrap:wrap;margin-top:6px;}
        .pill{
          font-size:10px;font-weight:600;letter-spacing:0.5px;
          padding:2px 8px;border-radius:12px;
          background:${dark?'rgba(0,212,255,0.08)':'rgba(0,120,200,0.08)'};
          border:1px solid ${dark?'rgba(0,212,255,0.2)':'rgba(0,120,200,0.2)'};
          color:${accent};
        }
        .dl-btn{
          display:flex;align-items:center;gap:7px;
          font-family:'Inter',sans-serif;font-size:12px;font-weight:700;
          letter-spacing:0.8px;text-transform:uppercase;
          background:${dark?'rgba(0,212,255,0.1)':'rgba(0,120,200,0.1)'};
          color:${accent};
          border:1.5px solid ${dark?'rgba(0,212,255,0.35)':'rgba(0,120,200,0.35)'};
          padding:10px 18px;border-radius:6px;cursor:none;
          transition:all 0.2s;white-space:nowrap;flex-shrink:0;
        }
        .dl-btn:hover,.dl-btn.flash{
          background:${accent};color:#fff;
          border-color:${accent};
          box-shadow:0 4px 18px ${dark?'rgba(0,212,255,0.3)':'rgba(0,120,200,0.3)'};
          transform:translateY(-1px);
        }
        .dl-arrow{
          width:18px;height:18px;display:flex;align-items:center;justify-content:center;
          border:1.5px solid currentColor;border-radius:50%;font-size:10px;
          flex-shrink:0;
        }

        /* dropzone */
        .dz{
          border:2px dashed ${dark?'rgba(0,212,255,0.2)':'rgba(0,120,200,0.22)'};
          background:${dark?'rgba(0,212,255,0.03)':'rgba(0,120,200,0.03)'};
          padding:48px 24px;text-align:center;cursor:none;
          transition:all 0.25s;margin-bottom:18px;border-radius:8px;
        }
        .dz.act,.dz:hover{
          border-color:${accent};
          background:${dark?'rgba(0,212,255,0.07)':'rgba(0,120,200,0.07)'};
        }
        .dz-icon{font-size:34px;margin-bottom:10px;}
        .dz-t{font-size:15px;font-weight:600;color:${text1};margin-bottom:4px;}
        .dz-s{font-size:12px;color:${text3};}
        .dz-f{font-size:14px;color:${accent};font-weight:600;}

        /* btn */
        .pbtn{
          width:100%;padding:16px;
          font-family:'Inter',sans-serif;font-size:13px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;
          background:${accent};color:#fff;border:none;cursor:none;
          clip-path:${btnClip};transition:all 0.2s;margin-bottom:18px;
          box-shadow:0 4px 20px ${dark?'rgba(0,212,255,0.22)':'rgba(0,120,200,0.22)'};
        }
        .pbtn:hover:not(:disabled){filter:brightness(1.08);transform:translateY(-2px);}
        .pbtn:disabled{background:${dark?'rgba(0,212,255,0.12)':'rgba(0,120,200,0.15)'};color:${text3};cursor:none;box-shadow:none;transform:none;}

        /* loading */
        .lb{
          border:1px solid ${border};background:${cardBg};
          padding:32px;text-align:center;margin-bottom:18px;border-radius:8px;
          box-shadow:${cardShad};
        }
        .lt{font-family:'Orbitron',monospace;font-size:12px;color:${accent};letter-spacing:2px;font-weight:700;}
        .lbar{height:3px;background:${dark?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.06)'};border-radius:2px;overflow:hidden;margin:16px 0;}
        .lbar-fill{height:100%;background:linear-gradient(90deg,transparent,${accent},transparent);animation:scan 1.4s linear infinite;}
        @keyframes scan{0%{transform:translateX(-100%);}100%{transform:translateX(300%);}}
        .ls{font-size:12px;color:${text3};margin-top:4px;}

        /* msgs */
        .ebox{border:1px solid ${dark?'rgba(255,60,60,0.3)':'rgba(200,30,30,0.2)'};background:${dark?'rgba(255,60,60,0.06)':'#fff5f5'};padding:13px 16px;margin-bottom:16px;font-size:13px;color:${dark?'#ff7777':'#cc2020'};border-radius:6px;}
        .sbox{border:1px solid ${dark?'rgba(0,255,136,0.2)':'rgba(0,160,80,0.2)'};background:${dark?'rgba(0,255,136,0.05)':'#f0fff7'};padding:13px 16px;margin-bottom:24px;font-size:13px;color:${dark?'#00ee88':'#007740'};border-radius:6px;display:flex;align-items:center;gap:8px;}

        /* stat grid */
        .sgrid{display:grid;grid-template-columns:repeat(3,1fr);gap:1px;background:${dark?'rgba(0,212,255,0.1)':'rgba(0,120,200,0.11)'};border:1px solid ${border};margin-bottom:24px;}
        .scell{background:${dark?'#060c18':'#fff'};padding:20px 16px;transition:background 0.2s;}
        .scell:hover{background:${dark?'rgba(0,212,255,0.05)':'rgba(0,120,200,0.04)'};}
        .sclabel{font-size:11px;font-weight:600;letter-spacing:1.5px;color:${text3};text-transform:uppercase;margin-bottom:6px;}
        .scval{font-family:'Orbitron',monospace;font-size:22px;font-weight:900;color:${accent};}
        .scunit{font-size:12px;color:${text3};margin-left:3px;}

        /* chart */
        .cpanel{border:1px solid ${border};background:${cardBg};padding:22px;margin-bottom:18px;border-radius:8px;box-shadow:${cardShad};}
        .ctitle{font-family:'Orbitron',monospace;font-size:11px;font-weight:700;color:${text1};letter-spacing:2px;margin-bottom:18px;text-transform:uppercase;}
        .ctitle span{color:${accent};}

        /* table */
        .rt{width:100%;border-collapse:collapse;font-size:13px;}
        .rt th{font-size:11px;font-weight:600;letter-spacing:1.5px;color:${text3};text-align:left;padding:10px 12px;border-bottom:1px solid ${border};text-transform:uppercase;}
        .rt td{padding:9px 12px;border-bottom:1px solid ${dark?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.04)'};color:${text2};font-weight:500;}
        .rt tr:hover td{background:${dark?'rgba(0,212,255,0.04)':'rgba(0,120,200,0.03)'};}
        .dg{color:${dark?'#00cc66':'#007740'};}
        .db{color:${dark?'#ff6666':'#cc2020'};}

        /* pdf btn */
        .pdfbtn{width:100%;padding:14px;font-family:'Inter',sans-serif;font-size:13px;font-weight:600;letter-spacing:1px;background:transparent;color:${dark?'#ff5577':'#cc2044'};border:1.5px solid ${dark?'rgba(255,68,102,0.35)':'rgba(200,30,60,0.25)'};cursor:none;transition:all 0.2s;margin-top:18px;border-radius:4px;}
        .pdfbtn:hover{background:${dark?'rgba(255,68,102,0.07)':'rgba(200,30,60,0.05)'};border-color:${dark?'rgba(255,68,102,0.6)':'rgba(200,30,60,0.5)'};}

        @media(max-width:700px){
          .sgrid{grid-template-columns:repeat(2,1fr);}
          .sample-banner{flex-direction:column;align-items:flex-start;}
          .dl-btn{width:100%;justify-content:center;}
        }
      `}</style>

      <div className="pr">
        {/* hero banner */}
        <div className="p-hero">
          <div className="p-hero-bg"/>
          <div className="p-hero-ov"/>
          <div className="p-hero-c">
            <div className="p-eyebrow"> ONNX Inference Engine</div>
            <div className="p-title">Predict 5G Delays</div>
            <div className="p-sub">Upload your packet trace CSV · Min 30 rows · SN + IP Delay (ms)</div>
          </div>
        </div>

        <div className="pi">

          {/* ── Sample File Download Banner ── */}
          <div className="sample-banner">
            <div className="sample-left">
              <div className="sample-icon">📊</div>
              <div>
                <div className="sample-title">Sample Test File — TE1.csv</div>
                <div className="sample-meta">Real 5G packet traces from KTH ExPECA Testbed, Sweden</div>
                <div className="sample-pills">
                  <span className="pill">3,500 packets</span>
                  <span className="pill">SN + IP Delay (ms)</span>
                  <span className="pill">KTH ExPECA</span>
                  <span className="pill">16–22 ms range</span>
                </div>
              </div>
            </div>
            <button
              className={`dl-btn${dlFlash?' flash':''}`}
              onClick={handleDownloadSample}
            >
              <div className="dl-arrow">↓</div>
              Download Sample
            </button>
          </div>

          {/* dropzone */}
          <div {...getRootProps()} className={`dz${isDragActive?' act':''}`}>
            <input {...getInputProps()}/>
            <div className="dz-icon">{file ? '📡' : '📂'}</div>
            {file
              ? <div className="dz-f">✓ {file.name}</div>
              : <><div className="dz-t">Drop CSV file here</div><div className="dz-s">or click to browse · .csv only</div></>
            }
          </div>

          <button className="pbtn" onClick={handlePredict} disabled={!file||loading}>
            {loading ? ` Processing… ${elapsed}s` : ' Run Prediction'}
          </button>

          {loading && (
            <div className="lb">
              <div className="lt">Running ONNX Inference</div>
              <div className="lbar"><div className="lbar-fill"/></div>
              <div className="ls">Elapsed: {elapsed}s · Sequence: 30 · Steps: 10</div>
            </div>
          )}

          {error && <div className="ebox">⚠ {error}</div>}

          {summary && (
            <>
              <div className="sbox">✓ Prediction complete — {summary.total_predictions} values generated</div>
              <div className="sgrid">
                {CARDS.map(c=>(
                  <div className="scell" key={c.l}>
                    <div className="sclabel">{c.l}</div>
                    <div className="scval">{c.v}<span className="scunit">{c.u}</span></div>
                  </div>
                ))}
              </div>
            </>
          )}

          {chartData && (
            <div className="cpanel">
              <div className="ctitle"> <span>Actual</span> vs <span>Predicted</span> Delay</div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={dark?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.06)'}/>
                  <XAxis dataKey="sn" stroke={text3} tick={{fill:text3,fontSize:11,fontFamily:'Inter'}} label={{value:'Sample',fill:text3,fontSize:11,position:'insideBottom',offset:-4}}/>
                  <YAxis stroke={text3} tick={{fill:text3,fontSize:11,fontFamily:'Inter'}} label={{value:'ms',fill:text3,fontSize:11,angle:-90,position:'insideLeft'}}/>
                  <Tooltip content={<CTooltip dark={dark}/>}/>
                  <Legend wrapperStyle={{fontFamily:'Inter',fontSize:12}}/>
                  <Line type="monotone" dataKey="Actual" stroke={lineA} dot={false} strokeWidth={1.5}/>
                  <Line type="monotone" dataKey="Predicted" stroke={accent} dot={false} strokeWidth={1.5}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {residData && (
            <div className="cpanel">
              <div className="ctitle"> <span>Residual</span> Error Analysis</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={residData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={dark?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.06)'}/>
                  <XAxis dataKey="sn" stroke={text3} tick={{fill:text3,fontSize:11}}/>
                  <YAxis stroke={text3} tick={{fill:text3,fontSize:11}}/>
                  <Tooltip content={<CTooltip dark={dark}/>}/>
                  <Bar dataKey="Residual" fill={dark?'rgba(0,212,255,0.5)':'rgba(0,120,200,0.5)'} stroke={accent} strokeWidth={0.5}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {results && (
            <div className="cpanel">
              <div className="ctitle"> Prediction <span>Results</span> Table</div>
              <table className="rt">
                <thead><tr><th>#</th><th>Actual (ms)</th><th>Predicted (ms)</th><th>Difference</th></tr></thead>
                <tbody>
                  {results.slice(0,50).map(r=>(
                    <tr key={r.sn}>
                      <td>{r.sn}</td><td>{r.actual}</td><td>{r.predicted}</td>
                      <td className={r.difference>0.5?'db':'dg'}>{r.difference}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {results && summary && (
            <button className="pdfbtn" onClick={()=>generatePDF(summary,results,file?.name)}>
              ⬇ Download PDF Intelligence Report
            </button>
          )}
        </div>
      </div>
    </>
  );
}
